import { useCallback, useEffect, useRef, type ReactNode } from "react";
import { clampSplit, MAX_SPLIT, MIN_SPLIT, useWorkspace } from "@/chat/workspace";
import { useMediaQuery } from "@/lib/useMediaQuery";
import { cn } from "@/lib/cn";

/**
 * The two-pane workspace: conversation on the left, artifacts on the right,
 * with a draggable divider between them (PRD FR-6.7).
 *
 * Below the breakpoint there is no divider and no split — the two panes become
 * tabs, because a 375px-wide column divided 58/42 is two unusable columns.
 *
 * The divider is a real `separator` with arrow-key support rather than a bare
 * mouse target: resizing a pane is a legitimate thing to want without a mouse,
 * and it costs a keydown handler.
 */
export function WorkspaceSplit({
  conversation,
  artifacts,
}: {
  conversation: ReactNode;
  artifacts: ReactNode;
}) {
  const wide = useMediaQuery("(min-width: 60rem)");
  const { split, tab, setSplit } = useWorkspace();
  const frame = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);

  const applyFromPointer = useCallback(
    (clientX: number) => {
      const bounds = frame.current?.getBoundingClientRect();
      if (!bounds || bounds.width === 0) return;
      setSplit(((clientX - bounds.left) / bounds.width) * 100);
    },
    [setSplit],
  );

  useEffect(() => {
    if (!wide) return;
    const move = (event: PointerEvent) => {
      if (!dragging.current) return;
      event.preventDefault();
      applyFromPointer(event.clientX);
    };
    const stop = () => {
      if (!dragging.current) return;
      dragging.current = false;
      document.body.style.removeProperty("cursor");
      document.body.style.removeProperty("user-select");
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", stop);
    window.addEventListener("pointercancel", stop);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", stop);
      window.removeEventListener("pointercancel", stop);
      stop();
    };
  }, [wide, applyFromPointer]);

  if (!wide) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        {tab === "conversation" ? conversation : artifacts}
      </div>
    );
  }

  return (
    <div ref={frame} className="flex min-h-0 flex-1">
      <div className="flex min-w-0 flex-col" style={{ width: `${split}%` }}>
        {conversation}
      </div>
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize the artifact panel"
        aria-valuenow={split}
        aria-valuemin={MIN_SPLIT}
        aria-valuemax={MAX_SPLIT}
        tabIndex={0}
        onPointerDown={(event) => {
          dragging.current = true;
          document.body.style.cursor = "col-resize";
          document.body.style.userSelect = "none";
          applyFromPointer(event.clientX);
        }}
        onDoubleClick={() => setSplit(58)}
        onKeyDown={(event) => {
          if (event.key === "ArrowLeft") setSplit(clampSplit(split - 2));
          if (event.key === "ArrowRight") setSplit(clampSplit(split + 2));
        }}
        className={cn(
          "group relative w-1.5 shrink-0 cursor-col-resize",
          "before:bg-border hover:before:bg-accent before:absolute before:inset-y-0",
          "before:start-1/2 before:w-px before:-translate-x-1/2 before:motion-fast",
        )}
      />
      <aside
        className="bg-surface flex min-w-0 flex-col overflow-y-auto"
        style={{ width: `${100 - split}%` }}
        aria-label="Artifact workspace"
      >
        {artifacts}
      </aside>
    </div>
  );
}
